import React, { useEffect, useState } from 'react';
import { AuditLogEventName, StyledDivider } from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { IAuditLogItemResponse } from 'models/auditLog';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useNavigate } from 'react-router-dom';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { ERouteLinks } from 'models/route';
import { useGlobalProperty } from 'models/globalPropertyContext';
import {
  createSafeStorageAddressList,
  getDictionaryValue,
  getLicenceStatusChipVariant,
  getPaymentStatusChipVariant,
} from 'pages/LegalEntitiesPage/LegalEntityViewLicencePage/helpers';
import { formatDate } from 'utils/date';
import { getHtmlText } from 'components/organisms/HtmlSlate';
import { EButtonSizes } from 'constants/Buttons';
import { useDictionary } from 'models/dictionaryContext';
import {
  IExtendedLegalEntityLicenceDetails,
  ILegalEntityLicenceDetails,
} from 'models/legalEntity';
import { colors } from 'theme/colors';
import { EAuditLogEvents } from '../helpers';
import { applicationStatusChips } from 'pages/LicencesPage/helpers';
import { ELicenceStatus } from 'pages/LegalEntitiesPage/helpers';
import { Chips } from 'components/atoms/Chips';

interface ILegalEntityLicencesEventDetails {
  auditLog: IAuditLogItemResponse;
}

export enum LETypes {
  individual = 'INDIVIDUAL',
  organization = 'ORGANIZATION',
}

interface IRenderDetails {
  licenceDetail: ILegalEntityLicenceDetails;
  t: any;
}
const RenderDetails = ({ licenceDetail, t }: IRenderDetails) => {
  const { shortDateFormat, getProperties } = useGlobalProperty();

  const licence = licenceDetail as unknown as IExtendedLegalEntityLicenceDetails;

  const {
    weaponLicencingAuthority,
    weaponLicencePaymentStatus,
    weaponLicenceGoodReason,
    country,
    weaponLicenceCompetency,
    region,
    weaponLicenceCategory,
    weaponLicenceCondition,
    getWeaponLicencingAuthority,
    getLegalType,
    getWeaponLicencePaymentStatus,
    getWeaponLicenceGoodReason,
    getCountry,
    getWeaponLicenceCompetency,
    getRegion,
    getWeaponLicenceCategory,
    getWeaponLicenceCondition,
  } = useDictionary();

  useEffect(() => {
    getWeaponLicencePaymentStatus();
    getWeaponLicencingAuthority();
    getLegalType();
    getWeaponLicenceGoodReason();
    getCountry();
    getWeaponLicenceCompetency();
    getRegion();
    getWeaponLicenceCategory();
    getWeaponLicenceCondition();
  }, []);

  useEffect(() => {
    getProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container spacing={2} rowSpacing={2} sx={{ marginBottom: '32px' }}>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.generalInformation.licenceName')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(licence?.general?.name)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.generalInformation.licenceCode')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(licence?.general?.code)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.generalInformation.licenceNumber')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(licence?.general?.licenceNumber)}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.generalInformation.licenceStatus')}
        </Typography>
        <Typography variant="body2">
          {applicationStatusChips(licence?.general?.status as ELicenceStatus, t)}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 / 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.generalInformation.startDate')}
        </Typography>
        <Typography variant="body2">
          {licence?.general?.startDate
            ? formatDate(licence.general.startDate, shortDateFormat)
            : emptyFieldHolder('')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 / 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.generalInformation.expiryDate')}
        </Typography>
        <Typography variant="body2">
          {licence?.general?.expiryDate
            ? formatDate(licence.general.expiryDate, shortDateFormat)
            : emptyFieldHolder('')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('caseManagement.cases.licenceAuthority.label')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(
            getDictionaryValue(
              licence?.general?.licenceAuthorityUniqueId,
              weaponLicencingAuthority,
            ),
          )}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.generalInformation.paymentStatus')}
        </Typography>
        <Typography variant="body2">
          <Chips
            text={getDictionaryValue(
              licence?.general?.paymentStatusUniqueId,
              weaponLicencePaymentStatus,
            )}
            variant={getPaymentStatusChipVariant(licence?.general?.paymentStatusUniqueId)}
            fullWidth
          />
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <StyledDivider />
        <Typography variant="subtitle1">
          {t('legalEntity.licenceDetails.conditions.header')}
        </Typography>
      </Grid>
      <Grid container size={{ xs: 12 }} spacing={2}>
        {licence?.conditions?.competencyUniqueId && (
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntity.licenceDetails.conditions.compentency')}
            </Typography>
            <Typography variant="body2">
              {getDictionaryValue(
                licence?.conditions?.competencyUniqueId,
                weaponLicenceCompetency,
              )}
            </Typography>
          </Grid>
        )}
        {licence?.conditions?.carry && (
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntity.licenceDetails.conditions.carryConditions')}
            </Typography>
            <Typography variant="body2">
              {getDictionaryValue(licence?.conditions?.carry, weaponLicenceCondition)}
            </Typography>
          </Grid>
        )}
        {licence?.conditions?.areaOfValidityUniqueId && (
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntity.licenceDetails.conditions.areaOfValidity')}
            </Typography>
            <Typography variant="body2">
              {getDictionaryValue(licence?.conditions?.areaOfValidityUniqueId, region)}
            </Typography>
          </Grid>
        )}
        {licence?.conditions?.weaponCategorisationUniqueId && (
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntity.licenceDetails.conditions.weaponCat')}
            </Typography>
            <Typography variant="body2">
              {getDictionaryValue(
                licence?.conditions?.weaponCategorisationUniqueId,
                weaponLicenceCategory,
              )}
            </Typography>
          </Grid>
        )}
        {licence?.conditions?.weaponNumberLimit > 0 && (
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntity.licenceDetails.conditions.weaponNumLimit')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(licence?.conditions?.weaponNumberLimit)}
            </Typography>
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>
            {t('legalEntity.licenceDetails.conditions.goodReason')}
          </Typography>
          {(() => {
            const goodReasonIds = licence?.conditions?.goodReasonUniqueIds;
            const goodReasonString = licence?.conditions?.goodReason;

            const hasValidIds =
              goodReasonIds && Array.isArray(goodReasonIds) && goodReasonIds.length > 0;

            const hasGoodReasonString =
              !hasValidIds && goodReasonString && goodReasonString.trim() !== '';

            if (!hasValidIds && !hasGoodReasonString) {
              return <Typography variant="body2">{emptyFieldHolder('')}</Typography>;
            }

            if (hasValidIds) {
              const availableReasons = weaponLicenceGoodReason || [];
              const selectedReasons = availableReasons.filter((reason) =>
                goodReasonIds.includes(String(reason.uniqueId)),
              );

              if (selectedReasons.length === 0) {
                return <Typography variant="body2">{emptyFieldHolder('')}</Typography>;
              }

              return (
                <Grid container>
                  {selectedReasons.map((reason) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={reason.uniqueId}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked
                            disabled
                            size={EButtonSizes.small}
                            sx={{
                              padding: '2px 4px 2px 0px',
                              '& input': {
                                width: '16px',
                                height: '16px',
                              },
                              '&.Mui-checked': {
                                color: colors.gray400,
                              },
                              '&.Mui-disabled': {
                                color: colors.gray400,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.875rem',
                            }}
                          >
                            {reason.name}
                          </Typography>
                        }
                        sx={{
                          margin: 0,
                          width: '100%',
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              );
            }

            return <Typography variant="body2">{goodReasonString}</Typography>;
          })()}
        </Grid>
        {licence?.conditions?.additionalConditions && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>
              {t('legalEntity.licenceDetails.conditions.additionalConditions')}
            </Typography>
            {getHtmlText(licence.conditions.additionalConditions)}
          </Grid>
        )}
      </Grid>
      <Grid size={{ xs: 12 }}>
        <StyledDivider />
        <Typography variant="subtitle1">
          {t('legalEntity.licenceDetails.safeStorage.header')}
        </Typography>
      </Grid>
      <Grid container size={{ xs: 12 }} spacing={2}>
        {(() => {
          if (licence?.conditions?.safeStorageDescription) {
            return (
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>
                  {t('legalEntity.licenceDetails.safeStorage.description')}
                </Typography>
                {getHtmlText(licence?.conditions?.safeStorageDescription)}
              </Grid>
            );
          }

          if (
            licence?.conditions?.safeStorages &&
            licence.conditions.safeStorages.length > 0
          ) {
            return (
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>
                  {t('legalEntity.licenceDetails.safeStorage.locations')}
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    color: '#111827',
                    fontSize: '0.875rem',
                    lineHeight: 1.43,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {createSafeStorageAddressList(licence.conditions.safeStorages, country, t)}
                </Typography>
              </Grid>
            );
          }

          return (
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: colors.gray500 }}>
                {t('legalEntity.licenceDetails.safeStorage.missing')}
              </Typography>
            </Grid>
          );
        })()}
      </Grid>
      {/* <Grid size={{ xs: 12 }}>
        <StyledDivider />
        <Typography variant="subtitle1">
          {t('legalEntity.licenceDetails.attachments.header')}
        </Typography>
      </Grid>
      <Grid container size={{ xs: 12 }} spacing={2}>
        aaa
      </Grid> */}
      <Grid size={{ xs: 12 }}>
        <StyledDivider />
        <Typography variant="subtitle1">
          {t('legalEntity.licenceDetails.additionalInfo.header')}
        </Typography>
      </Grid>
      <Grid container size={{ xs: 12 }} spacing={2}>
        {licence?.additionalInformation ? (
          <Grid size={{ xs: 12 }}>
            {licence?.additionalInformation}
          </Grid>
        ) : (
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: colors.gray500 }}>
              {t('legalEntity.licenceDetails.additionalInfo.missing')}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const LegalEntityLicencesEventDetails = ({ auditLog }: ILegalEntityLicencesEventDetails) => {
  const [licencesDetails, setLicencesDetails] = useState<any | null>();
  const [previouslicencesDetails, setPreviousLicencesDetails] = useState<any | null>();
  const { t } = useTranslations();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      if (auditLog && auditLog?.postObjectValue) {
        const postObjectValue =
          auditLog.postObjectValue as unknown as IExtendedLegalEntityLicenceDetails | null;
        setLicencesDetails(postObjectValue);
      }
      if (auditLog && auditLog?.previousObjectValue) {
        const previousObjectValue =
          auditLog.previousObjectValue as unknown as IExtendedLegalEntityLicenceDetails | null;
        setPreviousLicencesDetails(previousObjectValue);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditLog]);

  return (
    <>
      {previouslicencesDetails &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.PUBLISH_LICENCE_DEFINITION &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.UNPUBLISH_LICENCE_DEFINITION && (
          <AuditLogEventName>
            <Typography variant="h5">
              {auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_LICENCE_DEFINITION
                ? `${t('licenceConfiguration.header')} ${t(
                  'auditLogDetails.before.event.title',
                )}`
                : `${t('licenceConfiguration.header')} ${t(
                  'auditLogDetails.after.event.title',
                )}`}
            </Typography>
          </AuditLogEventName>
        )}

      {previouslicencesDetails &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.PUBLISH_LICENCE_DEFINITION &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.UNPUBLISH_LICENCE_DEFINITION && (
          <DetailsTemplate
            title={previouslicencesDetails?.general.name}
            pageType={
              auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LICENCE_DEFINITION
                ? EDetailsPageTypes.auditLogView
                : undefined
            }
            redirectToView={() =>
              navigate(
                `${ERouteLinks.LegalEntityViewLicence.replace(
                  ':id',
                  previouslicencesDetails?.owner?.legalUniqueId,
                ).replace(':licenceId', previouslicencesDetails?.uniqueId)}?type=${previouslicencesDetails?.owner?.legalType === LETypes.individual
                  ? 'individual'
                  : 'organisation'
                }`,
              )
            }
          >
            <RenderDetails licenceDetail={previouslicencesDetails} t={t} />
          </DetailsTemplate >
        )}

      {
        auditLog &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LICENCE_DEFINITION &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.LINKING_WEAPON_LICENCE && (
          <AuditLogEventName sx={{ marginTop: '20px' }}>
            <Typography variant="h5">
              {`${t('legalEntity.licenceDetails.title')} ${t(
                'auditLogDetails.post.event.title',
              )}`}
            </Typography>
          </AuditLogEventName>
        )
      }
      {
        licencesDetails && (
          <DetailsTemplate
            title={licencesDetails?.general.name}
            pageType={
              auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LICENCE_DEFINITION
                ? EDetailsPageTypes.auditLogView : undefined
            }
            redirectToView={() =>
              navigate(
                `${ERouteLinks.LegalEntityViewLicence.replace(
                  ':id',
                  licencesDetails?.owner?.legalUniqueId,
                ).replace(':licenceId', licencesDetails?.uniqueId)}?type=${licencesDetails?.owner?.legalType === LETypes.individual
                  ? 'individual'
                  : 'organisation'
                }`,
              )
            }
          >
            <RenderDetails licenceDetail={licencesDetails} t={t} />
          </DetailsTemplate >
        )}
    </>
  );
};

export default LegalEntityLicencesEventDetails;
